import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { USAddressFormData } from "./_useUSAddressFormData";

interface UseUSAddressValidationOptions {
  formData: USAddressFormData;
  isZipValid: MaybeRefOrGetter<boolean>;
}

/**
 * Manages the validation state for a US address.
 * @param options - The options object containing form data and ZIP code validity.
 * @returns An object containing validation state for each field and the overall address.
 */
export function useUSAddressValidation(options: UseUSAddressValidationOptions) {
  /**
   * Strict validity values represent the actual rule outcomes for each field,
   * independent of whether validation messages are currently shown in the UI.
   */
  const strictAddress1Valid = computed(
    () => options.formData.address1.trim() !== "",
  );
  const strictCityValid = computed(() => options.formData.city.trim() !== "");
  const strictStateValid = computed(() => !!options.formData.state);
  const strictZipValid = computed(() => toValue(options.isZipValid));

  const strictAddressValid = computed(
    () =>
      strictAddress1Valid.value &&
      strictCityValid.value &&
      strictStateValid.value &&
      strictZipValid.value,
  );

  return {
    strictAddress1Valid,
    strictCityValid,
    strictStateValid,
    strictZipValid,
    strictAddressValid,
  };
}
